import { Box, Group, useMantineTheme } from "@mantine/core"
import { InfoBox, Loader } from "@tfg/ui"
import _ from "lodash"
import { useTranslation } from "react-i18next"

import { GetDesignTokensErrorReason } from "@tfg/types/endpoints/communication-studio/get-design-tokens-query"

import {
  GetDesignTokensErrorResponseDto,
  GetDesignTokensQueryResponse,
  useGetDesignTokens,
  useGetProductPageComponents,
} from "~/gen/api"
import { i18n } from "~/i18n"
import { DesignTokensForm } from "~/pages/Brand/CommunicationStudio/DesignStudio/components/DesignTokensForm"
import { PreviewContainer } from "~/pages/Brand/CommunicationStudio/DesignStudio/components/PreviewContainer"
import { DesignTokensEditorProvider } from "~/pages/Brand/CommunicationStudio/DesignStudio/contexts/designTokenEditor/DesignTokensEditorContext"
import { getDesignTokensFromQueryResponse } from "~/pages/Brand/CommunicationStudio/DesignStudio/utils/utils"

export const DesignStudioWrapper = ({
  displaySettingsId,
}: {
  displaySettingsId: string
}) => {
  const { t } = useTranslation("communicationStudio")
  const theme = useMantineTheme()
  const { data, isPending, isRefetching } = useGetDesignTokens(
    displaySettingsId,
    {
      query: {
        enabled: !!displaySettingsId,
        refetchOnWindowFocus: false,
      },
    }
  )

  const {
    data: productPageComponentsData,
    isPending: isProductPageComponentsPending,
  } = useGetProductPageComponents(displaySettingsId, {
    query: {
      enabled: !!displaySettingsId,
    },
  })

  const productPageComponents = productPageComponentsData?.data ?? []

  if (isPending || isProductPageComponentsPending) {
    return <Loader />
  }
  if (data?.data && isErrorData(data?.data)) {
    const reason = isGetDesignTokensErrorReason(data.data.reason)
      ? data.data.reason
      : undefined
    return (
      <InfoBox
        title={t("design.error.title")}
        message={getDesignTokensErrorReason(reason)}
        variant="error"
      />
    )
  }

  const { stylingParameters, configurations } =
    getDesignTokensFromQueryResponse(data?.data) ?? {}
  if (
    _.isEmpty(stylingParameters) ||
    _.isEmpty(configurations) ||
    !productPageComponents
  ) {
    return (
      <InfoBox
        title={t("design.error.title")}
        message={t("design.error.generic")}
        variant="error"
      />
    )
  }
  return (
    <Group
      wrap="nowrap"
      h="100%"
      w="100%"
      pos="relative"
      style={{ overflowX: "scroll" }}
    >
      <DesignTokensEditorProvider
        key={isRefetching ? "refetching" : `${displaySettingsId}`}
        initialStyleData={stylingParameters}
        initialConfigurations={configurations}
        initialProductPageComponents={productPageComponents}
      >
        <Group h="100%" w="100%" pos="relative" wrap="nowrap">
          <Box
            style={{ flex: 1, minWidth: 0, maxWidth: "33%" }}
            h="100%"
            miw={theme.other.widths.card.width.md}
          >
            <DesignTokensForm />
          </Box>
          <Box
            h="100%"
            style={{
              flex: 1,
              maxWidth: "66%",
              minWidth: theme.other.widths.card.width.md,
            }}
          >
            <PreviewContainer productPageComponents={productPageComponents} />
          </Box>
        </Group>
      </DesignTokensEditorProvider>
    </Group>
  )
}

export const getDesignTokensErrorReason = (
  reason: GetDesignTokensErrorReason | undefined
) => {
  switch (reason) {
    case GetDesignTokensErrorReason.STYLING_PARAMETERS_NOT_FOUND:
      return i18n.t(
        "communicationStudio:design.error.stylingParametersNotFound"
      )
    case GetDesignTokensErrorReason.CONFIGURATIONS_NOT_FOUND:
      return i18n.t("communicationStudio:design.error.configurationsNotFound")
    default:
      return i18n.t("communicationStudio:design.error.generic")
  }
}

export const isErrorData = (
  obj: GetDesignTokensQueryResponse | GetDesignTokensErrorResponseDto
): obj is GetDesignTokensErrorResponseDto => {
  return obj && typeof obj === "object" && "reason" in obj && !obj.ok
}

export const isGetDesignTokensErrorReason = (
  value: string | undefined
): value is GetDesignTokensErrorReason => {
  if (!value) {
    return false
  }
  return Object.values(GetDesignTokensErrorReason).includes(value)
}
